import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, LifeBuoy, Building2, User, MessageSquare,
  ShieldCheck, UserCircle2, ChevronDown, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUSES = ["open", "in_progress", "waiting_on_garage", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

const STATUS_TONE = {
  open: "bg-[#F79009]/10 text-[#F79009]",
  in_progress: "bg-[#457b9d]/10 text-[#457b9d]",
  waiting_on_garage: "bg-[#e63946]/10 text-[#e63946]",
  resolved: "bg-[#12B76A]/10 text-[#12B76A]",
  closed: "bg-[#66708515] text-[#667085]",
};
const PRIORITY_TONE = {
  urgent: "bg-[#e63946]/10 text-[#e63946]",
  high: "bg-[#F79009]/10 text-[#F79009]",
  normal: "bg-[#457b9d]/10 text-[#457b9d]",
  low: "bg-[#66708515] text-[#667085]",
};

const Pill = ({ v, map }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[v] || map.open || map.normal}`}>
    {(v || "").replace(/_/g, " ")}
  </span>
);

export default function AdminSupportCaseDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [saving, setSaving] = useState(false);

  const canAct = ["platform_owner", "platform_admin", "support_admin"].includes(me?.admin_role);

  const load = () => {
    api.get(`/admin/support/cases/${cid}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load();   }, [cid]);

  useEffect(() => {
    if (!canAct) return;
    api.get("/admin/members").then((r) => setAdmins(r.data || [])).catch(() => {});
  }, [canAct]);

  const action = async (payload) => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/support/cases/${cid}/action`, payload);
      setData((prev) => ({ ...prev, ...r.data }));
      toast.success("Updated");
      if (payload.comment) { setComment(""); setInternal(false); }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-6 text-[13px] text-[#667085]">Loading case…</div>;
  const updates = data.updates || [];

  return (
    <div className="space-y-5" data-testid="admin-support-case-detail">
      <button
        onClick={() => nav("/admin/support/cases")}
        data-testid="admin-support-case-back"
        className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center gap-1"
      >
        <ArrowLeft size={13} /> Back to support cases
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Support Case</div>
            <div className="text-[14px] font-mono text-[#457b9d] font-semibold mb-1">{data.case_number}</div>
            <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight flex items-center gap-2" data-testid="admin-support-case-subject">
              <LifeBuoy size={20} className="text-[#457b9d]" />
              {data.subject}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Pill v={data.status} map={STATUS_TONE} />
              <Pill v={data.priority} map={PRIORITY_TONE} />
              <span className="text-[11px] text-[#667085] uppercase tracking-wider font-semibold">
                {data.category?.replace(/_/g, " ")}
              </span>
              <span className="text-[11px] text-[#667085] font-mono">Opened {fmtDateTime(data.created_at)}</span>
            </div>
            {data.description && (
              <div className="mt-4 text-[13px] text-[#1d3557] whitespace-pre-wrap bg-[#F7F9FC] rounded-md p-3 border border-[#E4E7EC]">
                {data.description}
              </div>
            )}
          </div>
          {canAct && (
            <div className="flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="admin-support-status-btn"
                    disabled={saving}
                    className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
                  >
                    Status <ChevronDown size={13} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1 min-w-[180px]">
                  {STATUSES.map((s) => (
                    <DropdownMenuItem
                      key={s}
                      onClick={() => action({ status: s })}
                      data-testid={`admin-support-status-${s}`}
                      className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 uppercase tracking-wider text-[11px] font-bold text-[#1d3557]"
                    >
                      {s.replace(/_/g, " ")}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="admin-support-priority-btn"
                    disabled={saving}
                    className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
                  >
                    Priority <ChevronDown size={13} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1 min-w-[140px]">
                  {PRIORITIES.map((p) => (
                    <DropdownMenuItem
                      key={p}
                      onClick={() => action({ priority: p })}
                      data-testid={`admin-support-priority-${p}`}
                      className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 uppercase tracking-wider text-[11px] font-bold text-[#1d3557]"
                    >
                      {p}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="admin-support-assignee-btn"
                    disabled={saving}
                    className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
                  >
                    {data.assignee_email ? `→ ${data.assignee_email.split("@")[0]}` : "Assign"}
                    <ChevronDown size={13} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1 min-w-[220px] max-h-72 overflow-y-auto">
                  <DropdownMenuItem
                    onClick={() => action({ assignee_id: "" })}
                    data-testid="admin-support-assignee-none"
                    className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2"
                  >
                    <UserCircle2 size={13} className="mr-2 text-[#a8dadc]" /> Unassign
                  </DropdownMenuItem>
                  {admins.filter((a) => a.active).map((a) => (
                    <DropdownMenuItem
                      key={a.id}
                      onClick={() => action({ assignee_id: a.id })}
                      data-testid={`admin-support-assignee-${a.id}`}
                      className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2"
                    >
                      <UserCircle2 size={13} className="mr-2 text-[#457b9d]" /> {a.name}
                      <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-[#457b9d]">{a.admin_role?.replace(/_/g, " ")}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Conversation */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="admin-support-conversation">
            <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold text-[#1d3557]">Conversation</h3>
                <div className="text-[11px] text-[#667085] mt-0.5">
                  {updates.length} message(s) · internal notes stay hidden from the garage
                </div>
              </div>
              <MessageSquare size={16} className="text-[#457b9d]" />
            </div>
            <div className="p-5 space-y-3">
              {updates.length === 0 ? (
                <div className="text-[13px] text-[#667085] text-center py-3">No updates yet.</div>
              ) : (
                updates.map((u, i) => {
                  const isAdmin = u.by_role === "admin";
                  const isInternal = u.kind === "internal";
                  return (
                    <div
                      key={i}
                      className={`rounded-md p-3 border ${
                        isInternal
                          ? "border-[#F79009]/40 bg-[#F79009]/5"
                          : isAdmin
                          ? "border-[#457b9d]/40 bg-[#457b9d]/5"
                          : "border-[#E4E7EC] bg-white"
                      }`}
                    >
                      <div className="text-[11px] font-semibold text-[#1d3557] flex items-center gap-1.5">
                        {isInternal ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#F79009]">
                            <Lock size={10} /> Internal
                          </span>
                        ) : isAdmin ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#457b9d]">
                            <ShieldCheck size={10} /> SparesHub
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1d3557]">
                            <User size={10} /> Garage
                          </span>
                        )}
                        · {u.by_name || u.by_email} · {fmtDateTime(u.at)}
                      </div>
                      <div className="mt-1.5 text-[13px] text-[#1d3557] whitespace-pre-wrap">{u.text}</div>
                    </div>
                  );
                })
              )}
              {canAct && (
                <div className="pt-3 border-t border-[#F1FAEE]">
                  <Textarea
                    rows={3}
                    data-testid="admin-support-comment-input"
                    placeholder="Reply to garage… or add an internal note."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="rounded-md"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <label className="inline-flex items-center gap-2 text-[12px] text-[#1d3557] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={internal}
                        onChange={(e) => setInternal(e.target.checked)}
                        data-testid="admin-support-internal-toggle"
                        className="accent-[#F79009]"
                      />
                      Internal only (hidden from garage)
                    </label>
                    <Button
                      onClick={() => comment.trim() && action({ comment: comment.trim(), internal })}
                      disabled={!comment.trim() || saving}
                      data-testid="admin-support-comment-save"
                      className="h-9 bg-[#1d3557] hover:bg-[#152941] rounded-md text-white font-semibold text-[13px] px-3"
                    >
                      Post message
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: garage + opener */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-support-garage">
            <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
              <Building2 size={12} /> Garage
            </div>
            {data.garage ? (
              <>
                <Link
                  to={`/admin/garages/${data.garage.id}`}
                  className="text-[15px] font-semibold text-[#1d3557] hover:text-[#e63946] block mt-1.5"
                >
                  {data.garage.name}
                </Link>
                <div className="text-[11px] text-[#667085] mt-1 font-mono">{data.garage.city || "—"}</div>
              </>
            ) : (
              <div className="text-[13px] text-[#667085] mt-1">—</div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-support-opener">
            <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
              <User size={12} /> Opened by
            </div>
            <div className="text-[13px] font-semibold text-[#1d3557] mt-1.5">{data.opened_by_name || data.opened_by_email}</div>
            <div className="text-[11px] text-[#667085] mt-0.5 font-mono">{data.opened_by_email}</div>
            {data.opened_by_user?.role && (
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#457b9d] mt-1">
                {data.opened_by_user.role.replace(/_/g, " ")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
